import { useContext, useEffect } from 'react';
import { AnalyticsContext } from '@shared/core';
import { useEventInfo } from '@shared/utils/eventInfo';
import { addError } from '@shared/utils/logger';

/**
 * Custom hook to generate a unique client reference string
 * by combining the event ID and the anonymous user ID.
 *
 * @param {string} [eventIdArg] - Optional event ID that overrides the one from event context. Pass it when the page has no event context.
 * @returns {{
 *   clientReference: string;
 *   loadingClientReference: boolean;
 *   anonymousId?: string;
 *   eventId?: string;
 * }}
 *
 * @example
 * const { clientReference, loadingClientReference } = useClientReference();
 */
export function useClientReference(eventIdArg?: string) {
  const { eventInfo, eventHandle, loading: loadingClientReference } = useEventInfo();
  const { anonymousId } = useContext(AnalyticsContext);

  const eventId = eventIdArg || eventInfo?.eventId || '';
  const clientReference = `${eventId}_${anonymousId}`;

  // Pages with no event handle and no eventIdArg (e.g. public stays under `/travel/*`) have no event by design.
  const isEventExpected = eventIdArg !== undefined || !!eventHandle;
  const isMissingEventId = !eventId && !loadingClientReference && isEventExpected;

  useEffect(() => {
    if (isMissingEventId) {
      addError(eventIdArg !== undefined ? 'ClientReference - Missing eventIdArg' : 'ClientReference - Missing eventId', { eventHandle });
    }
  }, [isMissingEventId, eventIdArg, eventHandle]);

  useEffect(() => {
    if (!anonymousId) {
      addError('ClientReference - Missing anonymousId');
    }
  }, [anonymousId]);

  return { clientReference, loadingClientReference, anonymousId, eventId: eventInfo?.eventId };
}
